import { useEffect, useState } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Building2,
  AlertCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_department_with_categories,
  get_department_with_categories,
  delete_department_with_categories
} from "../ServiceConnection/serviceconnection";
import { Form } from "react-router-dom";

// Mock data
const mockDepartments = [
  {
    id: "1",
    departmentName: "Kitchen",
    hasSubDepartments: true,
    subDepartments: ["Hot Kitchen", "Cold Kitchen", "Bakery", "Pastry"],
    createdAt: "2024-01-10",
    status: "active",
  },
  {
    id: "2",
    departmentName: "Bar",
    hasSubDepartments: true,
    subDepartments: ["Main Bar", "Pool Bar", "Rooftop Bar"],
    createdAt: "2024-01-12",
    status: "active",
  },
  {
    id: "3",
    departmentName: "Housekeeping",
    hasSubDepartments: false,
    subDepartments: [],
    createdAt: "2024-01-15",
    status: "active",
  },
  {
    id: "4",
    departmentName: "Maintenance",
    hasSubDepartments: true,
    subDepartments: ["Electrical", "Plumbing", "HVAC"],
    createdAt: "2024-01-18",
    status: "active",
  },
  {
    id: "5",
    departmentName: "Front Office",
    hasSubDepartments: false,
    subDepartments: [],
    createdAt: "2024-01-20",
    status: "active",
  },
];

export default function Departments() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [departments, setDepartments] = useState(mockDepartments);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  // Form states
  const [departmentName, setDepartmentName] = useState("");
  const [hasSubDepartments, setHasSubDepartments] = useState(false);
  const [has_is_main_store, setHas_is_main_store] = useState(false);
  const [subDepartments, setSubDepartments] = useState([""]);
  const [errors, setErrors] = useState({});

  // Filter departments
  const filteredDepartments = departments.filter((dept) =>
    dept.departmentName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reset form
  const resetForm = () => {
  setDepartmentName("");
  setHasSubDepartments(false);
  setHas_is_main_store(false);
  setSubDepartments([""]);
  setErrors({});
  setEditingDepartment(null);
};

  // Open dialog for create
  const handleCreateClick = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Open dialog for edit
  const handleEditClick = (department) => {
    setEditingDepartment(department);
    setDepartmentName(department.departmentName);
    setHasSubDepartments(department.hasSubDepartments);
    setSubDepartments(
      department.subDepartments.length > 0 ? department.subDepartments : [""]
    );
    setHas_is_main_store(
  Number(department.is_main_store) === 1
);
    setErrors({});
    setIsDialogOpen(true);
  };

  // Add sub-department field
  const handleAddSubDepartment = () => {
    setSubDepartments([...subDepartments, ""]);
  };

  // Remove sub-department field
  const handleRemoveSubDepartment = (index) => {
    const updated = subDepartments.filter((_, i) => i !== index);
    setSubDepartments(updated.length > 0 ? updated : [""]);
  };

  // Update sub-department value
  const handleSubDepartmentChange = (index, value) => {
    const updated = [...subDepartments];
    updated[index] = value;
    setSubDepartments(updated);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!departmentName.trim()) {
      newErrors.departmentName = "Department name is required";
    }

    if (hasSubDepartments) {
      const filledSubDepts = subDepartments.filter((s) => s.trim() !== "");
      if (filledSubDepts.length === 0) {
        newErrors.subDepartments = "At least one sub-department is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit (create or update)
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const filteredSubDepts = hasSubDepartments
        ? subDepartments.filter((s) => s.trim() !== "")
        : [];

      // 🔹 Prepare FormData
      const fd = new FormData();
      fd.append("department_name", departmentName.trim());
      fd.append("categories", JSON.stringify(filteredSubDepts));
      fd.append(
        "is_main_store",
        has_is_main_store ? 1 : 0
      );
      // 🔹 If editing, send department id
      if (editingDepartment) {
        fd.append("primary_id", editingDepartment.id);
      }

      // 🔹 API Call
      const response = await server_post_data(
        save_update_department_with_categories,
        fd
      );

      // Possible structure 1
    if (response?.error === true) {
      toast({
        title: "Error",
        description: response?.message,
        variant: "destructive",
      });
      return;
    }

    // Possible structure 2 (axios style)
    if (response?.data?.error === true) {
      toast({
        title: "Error",
        description: response?.data?.message,
        variant: "destructive",
      });
      return;
    }

      console.log("API Response:", response);

      // 🔹 Update UI only after API success
      if (editingDepartment) {
        setDepartments(
          departments.map((dept) =>
            dept.id === editingDepartment.id
              ? {
                  ...dept,
                  departmentName: departmentName.trim(),
                  hasSubDepartments,
                  subDepartments: filteredSubDepts,
                }
              : dept
          )
        );

        toast({
          title: "Success",
          description: "Department updated successfully",
        });
      } else {
        const newDepartment = {
          id: response?.id || Date.now().toString(), // backend id preferred
          departmentName: departmentName.trim(),
          hasSubDepartments,
          subDepartments: filteredSubDepts,
          is_main_store: has_is_main_store,
          createdAt: new Date().toISOString().split("T")[0],
          status: "active",
        };

        setDepartments((prev) => [...prev, newDepartment]);

        toast({
          title: "Success",
          description: "Department created successfully",
        });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Save department error:", error);

      toast({
        title: "Error",
        description: "Failed to save department. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    master_data_get();
  }, []);

  const master_data_get = async () => {
    try {
      const formData = new FormData();

      const response = await server_post_data(
        get_department_with_categories,
        formData
      );
      const apiData = response.data.message.data || [];
      const mappedDepartments = apiData.map((dept) => {
        const subDepartments =
          dept.categories?.map((cat) => cat.dcategory_name) || [];

        return {
          id: dept.department_id.toString(),
          departmentName: dept.department_name,
          hasSubDepartments: subDepartments.length > 0,
          subDepartments: subDepartments,
          is_main_store: Number(dept.is_main_store) === 1,
          createdAt: dept.categories?.[0]?.entry_date
            ? dept.categories[0].entry_date.split("T")[0]
            : "",
          status: "active",
        };
      });

      setDepartments(mappedDepartments);

      console.log("Mapped Departments:", mappedDepartments);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const hasMainStore = departments.some(
  (dept) => Number(dept.is_main_store) === 1
);

  // Open delete confirmation
  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
const handleConfirmDelete = async () => {
  if (!departmentToDelete) return;

  try {
    const fd = new FormData();
    fd.append("primary_id", departmentToDelete.id);

    // 🔹 Call backend first
    const response = await server_post_data(
      delete_department_with_categories,
      fd
    );

    // 🔹 Optional: check backend success flag
    if (response?.error) {
      throw new Error("Delete failed");
    }

    // 🔹 Update UI after success
    setDepartments((prev) =>
      prev.filter((dept) => dept.id !== departmentToDelete.id)
    );

    toast({
      title: "Deleted",
      description: `${departmentToDelete.departmentName} has been deleted`,
    });

    setIsDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  } catch (error) {
    console.error("Delete department error:", error);

    toast({
      title: "Error",
      description: "Failed to delete department. Please try again.",
      variant: "destructive",
    });
  }
};


  return (
    <MainLayout>
      <Header
        title="Department Management"
        subtitle="Manage departments and sub-departments"
      />

      <div className="p-6 space-y-6">
        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={handleCreateClick}
            className="gap-2 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
          >
            <Plus className="w-4 h-4" />
            New Department
          </Button>
        </div>

        {/* Departments Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Department Name
                </TableHead>
                {/* <TableHead className="text-muted-foreground">
                  Sub-Departments
                </TableHead> */}
                <TableHead className="text-muted-foreground">
                  Created Date
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDepartments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Building2 className="w-8 h-8" />
                      <p>No departments found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDepartments.map((department) => (
                  <TableRow
                    key={department.id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-white" />
                        </div>
                        <span className="font-medium text-foreground">
                          {department.departmentName}
                        </span>
                      </div>
                    </TableCell>
                    {/* <TableCell>
                      {department.hasSubDepartments ? (
                        <div className="flex flex-wrap gap-1">
                          {department.subDepartments.map((subDept, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              {subDept}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          No sub-departments
                        </span>
                      )}
                    </TableCell> */}
                    <TableCell className="text-muted-foreground text-sm">
                      {department.createdAt}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-success/20 text-success border-success/30 font-medium border">
                        Active
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">

                        {!department.is_main_store && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(department)}
                              className="hover:bg-cyan-50 hover:text-cyan-600"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(department)}
                              className="hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}

                        {department.is_main_store && (
                          <Badge>
                            Main Store
                          </Badge>
                        )}

                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDepartment
                  ? "Edit Department"
                  : "Create New Department"}
              </DialogTitle>
              <DialogDescription>
                {editingDepartment
                  ? "Update department details and sub-departments"
                  : "Add a new department to your restaurant"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Department Name */}
              <div className="space-y-2">
                <Label htmlFor="departmentName">
                  Department Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="departmentName"
                  placeholder="e.g., Kitchen, Bar, Housekeeping"
                  value={departmentName}
                  onChange={(e) => setDepartmentName(e.target.value)}
                  className={errors.departmentName ? "border-destructive" : ""}
                />
                {errors.departmentName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.departmentName}
                  </p>
                )}
              </div>

              {/* Has Sub-Departments Checkbox */}
              {!hasMainStore && !editingDepartment && (
                <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
                  <Checkbox
                    id="has_is_main_store"
                    checked={has_is_main_store}
                    onCheckedChange={setHas_is_main_store}
                  />
                  <label
                    htmlFor="has_is_main_store"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Mark as main store
                  </label>
                </div>
              )}

              {hasMainStore && "1"!="1" && (
                <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
                  <Checkbox
                    id="hasSubDepartments"
                    checked={hasSubDepartments}
                    onCheckedChange={setHasSubDepartments}
                  />
                  <label
                    htmlFor="hasSubDepartments"
                    className="text-sm font-medium cursor-pointer"
                  >
                    This department has sub-departments
                  </label>
                </div>
              )}

              {/* Sub-Departments (conditional) */}
              {hasSubDepartments && (
                <div className="space-y-3 p-4 border rounded-lg bg-cyan-50/50">
                  <div className="flex items-center justify-between">
                    <Label>
                      Sub-Departments{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddSubDepartment}
                      className="gap-2"
                    >
                      <Plus className="w-3 h-3" />
                      Add More
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {subDepartments.map((subDept, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder={`Sub-department ${index + 1}`}
                          value={subDept}
                          onChange={(e) =>
                            handleSubDepartmentChange(index, e.target.value)
                          }
                          className="flex-1"
                        />
                        {subDepartments.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSubDepartment(index)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {errors.subDepartments && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.subDepartments}
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
              >
                {editingDepartment ? "Update Department" : "Create Department"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Department</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{departmentToDelete?.departmentName}</strong>"?
                {departmentToDelete?.hasSubDepartments && (
                  <span className="block mt-2 text-destructive">
                    This will also remove all{" "}
                    {departmentToDelete.subDepartments.length} sub-departments.
                  </span>
                )}
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
